import React, { Fragment, useContext, useState } from 'react';
import { useLocation, useMatch } from 'react-router-dom';
import henceforthApi from '../../../utils/henceforthApi';
import { GlobalContext, handleError } from '../../../context/Provider';
import Spinner from '../../../components/common/spinner';
import BreadCrumb from '../../../components/common/BreadCrumb';
import commonArray from '../../../components/common/commonArray';
import { Button } from '../../../components/ui/button';
import { ShieldCheck, Save, AlertCircle } from 'lucide-react';

const EditService: React.FC = () => {
  const match = useMatch('/product/:product_id/services/edit/:service_id');
  const { language_data, authState, loading, setLoading, toastMessage, authDispatch } =
    useContext(GlobalContext);

  const breadCrumbPath = [
    {
      name: `${language_data?.[authState.lang]?.common_home || 'Home'}`,
      url: `/`,
      active: '',
    },
    {
      name: `${language_data?.[authState.lang]?.common_product || 'Product'} ${language_data?.[authState.lang]?.common_list || 'List'
        }`,
      url: `/products/1`,
      active: '',
    },
    {
      name: `${language_data?.[authState.lang]?.common_product || 'Product'} ${language_data?.[authState.lang]?.common_deatil || 'Detail'
        }`,
      url: `/product/${match?.params.product_id}`,
      active: '',
    },
    {
      name: `${language_data?.[authState.lang]?.common_edit || 'Edit'} ${language_data?.[authState.lang]?.common_services || 'Service'
        }`,
      url: ``,
      active: 'not-allowed',
    },
  ];

  const location = useLocation();
  const newParam = new URLSearchParams(location.search);
  const [service, setService] = useState<string>((newParam.get('service') as string) || '');
  const [servicesErr, setServicesErr] = useState<string>('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!service) {
      return setServicesErr(
        `${language_data?.[authState.lang]?.common_services || 'Service'} ${language_data?.[authState.lang]?.common_is || 'is'
        } ${language_data?.[authState.lang]?.common_required || 'required'}`
      );
    }
    if (!service.trim()) {
      return setServicesErr(
        `${language_data?.[authState.lang]?.common_please || 'Please'} ${language_data?.[authState.lang]?.common_write || 'write'
        } ${language_data?.[authState.lang]?.common_something || 'something'} ${language_data?.[authState.lang]?.common_in || 'in'
        } ${language_data?.[authState.lang]?.common_services || 'services'}`
      );
    }

    const items = {
      _id: match?.params.service_id,
      product_id: match?.params.product_id,
      content: service.trim(),
      language: 'ENGLISH',
    };

    setLoading(true);
    try {
      const editServiceRes = await henceforthApi.Product.editProductDetailsDynamically(
        commonArray.productOtherDetails[4],
        items
      );
      toastMessage(editServiceRes.message || 'Service updated successfully');
      window.history.back();
    } catch (err) {
      handleError(err, 'active', authDispatch);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setService(e.target.value);
    setServicesErr('');
  };

  return (
    <Fragment>
      <BreadCrumb pathNameDeclare={breadCrumbPath} />

      <div className="w-full px-4 sm:px-6 lg:px-8 py-4 space-y-4">
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs p-4 sm:p-4 space-y-4">
          {/* Header */}
          <div className="flex items-center gap-3.5 pb-4 border-b border-slate-100">
            <div className="w-10 h-10 rounded-xl bg-sky-50 text-[#1AA7DE] flex items-center justify-center shrink-0 shadow-2xs">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-bold text-slate-900 tracking-tight m-0">
                {language_data?.[authState.lang]?.common_edit || 'Edit'}{' '}
                {language_data?.[authState.lang]?.common_services || 'Product Service'}
              </h2>
              <p className="text-xs text-slate-500 font-medium m-0 mt-0.5">
                Update buyer guarantees, warranty policy, or support terms
              </p>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-1.5">
              <label className="block text-xs sm:text-sm font-bold text-slate-700 tracking-wide">
                {language_data?.[authState.lang]?.common_services || 'Service Description'}{' '}
                <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                value={service}
                onChange={handleChange}
                placeholder={
                  language_data?.[authState.lang]?.common_enter_your
                    ? `${language_data?.[authState.lang]?.common_enter_your} ${language_data?.[authState.lang]?.common_services}`
                    : 'e.g. 1 Year Official Manufacturer Warranty, 7-Day Replacement'
                }
                className={`w-full px-4 py-2.5 rounded-xl border text-xs sm:text-sm font-medium transition-all focus:outline-none focus:ring-2 ${servicesErr
                    ? 'border-rose-300 bg-rose-50/30 text-rose-900 focus:ring-rose-200 focus:border-rose-500'
                    : 'border-slate-200 bg-slate-50/50 hover:bg-white focus:bg-white text-slate-800 focus:ring-[#1AA7DE]/20 focus:border-[#1AA7DE]'
                  }`}
              />
              {servicesErr && (
                <div className="flex items-center gap-1.5 text-xs font-semibold text-rose-600 pt-1">
                  <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                  <span>{servicesErr}</span>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-slate-100">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => window.history.back()}
                disabled={loading}
              >
                {language_data?.[authState.lang]?.common_cancel || 'Cancel'}
              </Button>
              <Button
                type="submit"
                variant="default"
                size="sm"
                disabled={loading}
                className="gap-1.5"
              >
                {loading ? (
                  <Spinner color="text-white" />
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span>{language_data?.[authState.lang]?.common_edit || 'Update Service'}</span>
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Fragment>
  );
};

export default EditService;